import React, { useState, useRef, useCallback, useEffect } from "react";
import QRCode from "qrcode";
import { Share2, Download, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatPrice, formatKm } from "@/lib/format";
import { useT } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F2540";
const SILVER = "#C9D2DC";

const FORMATS = {
  portrait: { W: 1080, H: 1350, ph: 860 },
  square: { W: 1080, H: 1080, ph: 560 },
  story: { W: 1080, H: 1920, ph: 1240 },
};

const _imgCache = new Map();
function loadImage(src) {
  if (!src) return Promise.reject(new Error("no src"));
  if (_imgCache.has(src)) return _imgCache.get(src);
  const p = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  _imgCache.set(src, p);
  p.catch(() => _imgCache.delete(src));
  return p;
}
function proxied(url) {
  if (!url) return null;
  if (/^data:/i.test(url)) return url;
  // Our own stored media is same-origin & CORS-enabled → load it directly.
  // Proxying it makes the backend fetch itself (502) and the photo disappears.
  if (/\/api\/media\/file\//.test(url)) return url;
  return `${BACKEND}/api/media/proxy?url=${encodeURIComponent(url)}`;
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export const SocialPoster = ({ car, imageUrl, images = [], dealer }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [fmt, setFmt] = useState("portrait");
  const [photoIdx, setPhotoIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showDealer, setShowDealer] = useState(true);
  const canvasRef = useRef(null);
  const qrRef = useRef({ key: null, url: null });

  const photos = (Array.isArray(images) && images.length ? images : (imageUrl ? [imageUrl] : [])).filter(Boolean);
  const activeImage = photos[photoIdx] || imageUrl;
  const hasDealerLogo = !!dealer?.logo;

  // Reset framing when the photo or format changes.
  useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [photoIdx, fmt]);

  const generate = useCallback(async (format) => {
    setBusy(true);
    const { W, H, ph } = FORMATS[format] || FORMATS.portrait;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // ── Pactly Electric theme — only for EV / hybrid listings (not thermal) ──
    const GREEN = "#39FF6A", BLUE = "#00F0FF";
    const fuelStr = String(car.fuel || car.fuel_type || "").toLowerCase();
    const isElectric = /électr|electr/.test(fuelStr) || car.ev === true || fuelStr === "bev";
    const isHybrid = /hybri|phev/.test(fuelStr) || fuelStr.includes("plug-in") || fuelStr.includes("plugin");
    const evTheme = isElectric || isHybrid;
    const evLabel = (isHybrid && isElectric) ? "HYBRIDE RECHARGEABLE"
      : (isHybrid && !isElectric) ? "HYBRIDE" : "100% ÉLECTRIQUE";
    const gradGB = (x0, x1) => {
      const g = ctx.createLinearGradient(x0, 0, x1, 0);
      g.addColorStop(0, GREEN); g.addColorStop(1, BLUE); return g;
    };
    const drawBolt = (bx, by, s, color) => {
      ctx.save(); ctx.fillStyle = color; ctx.beginPath();
      ctx.moveTo(bx + s * 0.55, by); ctx.lineTo(bx + s * 0.15, by + s * 0.58);
      ctx.lineTo(bx + s * 0.45, by + s * 0.58); ctx.lineTo(bx + s * 0.30, by + s);
      ctx.lineTo(bx + s * 0.80, by + s * 0.40); ctx.lineTo(bx + s * 0.48, by + s * 0.40);
      ctx.lineTo(bx + s * 0.70, by); ctx.closePath(); ctx.fill(); ctx.restore();
    };

    // Flat navy background
    ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, H);

    // Photo area (cover-fit) with user pan/zoom, clamped to always cover the frame.
    try {
      const img = await loadImage(proxied(activeImage));
      const base = Math.max(W / img.width, ph / img.height);
      const scale = base * zoom;
      const dw = img.width * scale, dh = img.height * scale;
      let dx = (W - dw) / 2 + offset.x;
      let dy = (ph - dh) / 2 + offset.y;
      dx = Math.min(0, Math.max(W - dw, dx));
      dy = Math.min(0, Math.max(ph - dh, dy));
      ctx.drawImage(img, dx, dy, dw, dh);
    } catch { /* keep navy background */ }

    // Silver top rule — green→blue energy line for EV/hybrid
    ctx.fillStyle = evTheme ? gradGB(0, W) : SILVER; ctx.fillRect(0, 0, W, 8);

    // Legibility shade at top
    const topShade = ctx.createLinearGradient(0, 0, 0, 210);
    topShade.addColorStop(0, "rgba(15,37,64,0.78)"); topShade.addColorStop(1, "rgba(15,37,64,0)");
    ctx.fillStyle = topShade; ctx.fillRect(0, 0, W, 210);

    // Logo lockup (latest brand, white version for the navy background)
    ctx.textBaseline = "top";
    try {
      const logo = await loadImage("/pactly-lockup-dark.png?v=3");
      const lw = 340, lh = lw * (logo.height / logo.width);
      ctx.drawImage(logo, 48, 48, lw, lh);
    } catch {
      ctx.font = "800 46px Sora, Inter, Arial, sans-serif";
      ctx.fillStyle = "#ffffff"; ctx.fillText("Pactly", 48, 44);
      const pw = ctx.measureText("Pactly ").width;
      ctx.fillStyle = SILVER; ctx.fillText("Car", 48 + pw, 44);
    }

    ctx.fillStyle = SILVER;
    roundRect(ctx, 48, 138, 244, 50, 25); ctx.fill();
    ctx.fillStyle = NAVY; ctx.font = "800 23px Sora, Inter, Arial, sans-serif";
    ctx.fillText("✓ Vérifié · BE/EU", 70, 152);

    // Co-branding — logo du concessionnaire (haut droite), optionnel.
    if (showDealer && dealer?.logo) {
      try {
        const dlogo = await loadImage(proxied(dealer.logo));
        const box = 96, bx = W - 48 - box, by = 44, pad = 12;
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = 14; ctx.shadowOffsetY = 4;
        ctx.fillStyle = "#ffffff"; roundRect(ctx, bx, by, box, box, 20); ctx.fill();
        ctx.restore();
        const availW = box - pad * 2, availH = box - pad * 2;
        const sc = Math.min(availW / dlogo.width, availH / dlogo.height);
        const dw = dlogo.width * sc, dh = dlogo.height * sc;
        ctx.drawImage(dlogo, bx + (box - dw) / 2, by + (box - dh) / 2, dw, dh);
        if (dealer.name) {
          ctx.fillStyle = SILVER; ctx.font = "600 20px Sora, Inter, Arial, sans-serif";
          ctx.textAlign = "right";
          ctx.fillText(String(dealer.name).slice(0, 22), bx - 14, by + box / 2 - 12);
          ctx.textAlign = "left";
        }
      } catch { /* dealer logo optional */ }
    }

    // Bottom panel (flat navy)
    ctx.fillStyle = NAVY; ctx.fillRect(0, ph, W, H - ph);

    // EV/hybrid signature — restrained glass badge on the photo, above the panel
    if (evTheme) {
      ctx.save();
      ctx.font = "700 26px Sora, Inter, Arial, sans-serif";
      try { ctx.letterSpacing = "3px"; } catch { /* older browsers */ }
      const tw = ctx.measureText(evLabel).width;
      const padL = 60, padR = 30, bh = 56, bw = padL + tw + padR;
      const bx = 48, by = ph - bh - 30;
      ctx.fillStyle = "rgba(9,16,34,0.60)";
      roundRect(ctx, bx, by, bw, bh, 14); ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = gradGB(bx, bx + bw);
      roundRect(ctx, bx, by, bw, bh, 14); ctx.stroke();
      drawBolt(bx + 24, by + 16, 26, GREEN);
      ctx.fillStyle = "#ffffff"; ctx.textBaseline = "middle";
      ctx.fillText(evLabel, bx + padL, by + bh / 2 + 1);
      ctx.textBaseline = "top";
      try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
      ctx.restore();
    }

    // Accent tab under the panel top — green→blue for EV, silver otherwise
    ctx.fillStyle = evTheme ? gradGB(48, 168) : SILVER; ctx.fillRect(48, ph + 22, 120, 5);

    // Hulk-green signature line at the photo ↔ description seam (EV/hybrid only)
    if (evTheme) {
      ctx.save();
      ctx.shadowColor = "rgba(57,255,106,0.45)"; ctx.shadowBlur = 16; ctx.shadowOffsetY = 2;
      ctx.fillStyle = GREEN; ctx.fillRect(0, ph - 2, W, 5);
      ctx.restore();
    }

    // Title
    const title = `${car.make || ""} ${car.model || ""}`.trim();
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 60px Sora, Inter, Arial, sans-serif";
    const maxW = W - 96;
    const words = title.split(" ");
    let line = "", y = ph + 42;
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, 48, y); y += 68; line = w; }
      else line = test;
    }
    if (line) { ctx.fillText(line, 48, y); y += 68; }

    // Chips
    const chips = [];
    if (car.year) chips.push(String(car.year));
    if (car.mileage) chips.push(formatKm(car.mileage));
    if (car.fuel) chips.push(car.fuel);
    if (car.power) chips.push(`${car.power} ch`);
    ctx.font = "600 30px Sora, Inter, Arial, sans-serif";
    let cx = 48; const cy = y + 6;
    for (const c of chips) {
      const cw = ctx.measureText(c).width + 44;
      if (cx + cw > W - 300) break;
      const isFuelChip = evTheme && c === car.fuel;
      if (isFuelChip) {
        ctx.fillStyle = "rgba(57,255,106,0.10)";
        roundRect(ctx, cx, cy, cw, 54, 27); ctx.fill();
        ctx.lineWidth = 2; ctx.strokeStyle = gradGB(cx, cx + cw);
        roundRect(ctx, cx, cy, cw, 54, 27); ctx.stroke();
        ctx.fillStyle = GREEN; ctx.fillText(c, cx + 22, cy + 12);
      } else {
        ctx.fillStyle = "rgba(255,255,255,0.14)";
        roundRect(ctx, cx, cy, cw, 54, 27); ctx.fill();
        ctx.fillStyle = "#ffffff"; ctx.fillText(c, cx + 22, cy + 12);
      }
      cx += cw + 14;
    }

    // Price
    ctx.font = "900 88px Sora, Inter, Arial, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(car.price ? formatPrice(car.price) : "—", 48, cy + 92);

    // EV/hybrid tagline under the price (electric identity punchline)
    if (evTheme) {
      const slogan = (isHybrid && !isElectric)
        ? "Hybride rechargeable · Faible consommation"
        : "Zéro émission · Recharge rapide";
      const sy = cy + 92 + 98;
      drawBolt(48, sy - 1, 28, GREEN);
      ctx.save();
      try { ctx.letterSpacing = "1px"; } catch { /* noop */ }
      ctx.font = "700 30px Sora, Inter, Arial, sans-serif";
      ctx.fillStyle = GREEN; ctx.textBaseline = "middle";
      ctx.fillText(slogan, 86, sy + 13);
      ctx.textBaseline = "top";
      try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
      ctx.restore();
    }

    // QR (bottom-right) — the scan hint stays on the right, next to the code.
    try {
      if (qrRef.current.key !== window.location.href) {
        qrRef.current.url = await QRCode.toDataURL(window.location.href, { margin: 1, width: 260, color: { dark: NAVY, light: "#ffffff" } });
        qrRef.current.key = window.location.href;
      }
      const qrImg = await loadImage(qrRef.current.url);
      const qs = 200, qx = W - qs - 48, qy = H - qs - 78;
      ctx.fillStyle = "#ffffff";
      roundRect(ctx, qx - 14, qy - 14, qs + 28, qs + 28, 20); ctx.fill();
      ctx.drawImage(qrImg, qx, qy, qs, qs);
      ctx.fillStyle = SILVER;
      ctx.font = "600 23px Sora, Inter, Arial, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(t("poster.scan"), W - 48, H - 52);
      ctx.textAlign = "left";
    } catch { /* QR optional */ }

    // Site URL — bottom-LEFT so it never sits under the QR / scanner zone.
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 26px Sora, Inter, Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("pactlycar.com", 48, H - 58);

    setDataUrl(canvas.toDataURL("image/png"));
    setBusy(false);
  }, [car, activeImage, dealer, showDealer, zoom, offset, t]);

  useEffect(() => { if (open) { const id = setTimeout(() => generate(fmt), 60); return () => clearTimeout(id); } }, [open, fmt, generate]);

  const dragRef = useRef(null);
  const previewRef = useRef(null);
  const onPointerDown = (e) => {
    if (!dataUrl) return;
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const cw = el.clientWidth || 1;
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y, scale: (FORMATS[fmt].W / cw) };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current; if (!d) return;
    setOffset({ x: d.ox + (e.clientX - d.x) * d.scale, y: d.oy + (e.clientY - d.y) * d.scale });
  };
  const onPointerUp = (e) => { dragRef.current = null; e.currentTarget?.releasePointerCapture?.(e.pointerId); };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `pactly-${fmt}-${(car.make || "car")}-${(car.model || "")}.png`.toLowerCase().replace(/\s+/g, "-");
    a.click();
  };

  const share = async () => {
    if (!dataUrl || !navigator.canShare) { download(); return; }
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "pactly-car.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${car.make} ${car.model}`, text: `${car.make} ${car.model} — ${formatPrice(car.price)}` });
      } else { download(); }
    } catch { /* cancelled */ }
  };

  const FMT_BTN = [
    { k: "portrait", label: t("poster.fmtPortrait") },
    { k: "square", label: t("poster.fmtSquare") },
    { k: "story", label: t("poster.fmtStory") },
  ];

  return (
    <>
      <button type="button" data-testid="poster-create-btn" onClick={() => setOpen(true)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-[#1E5288]/40 bg-[#1E5288]/10 py-2.5 text-sm font-semibold text-[#1E5288] transition hover:bg-[#1E5288]/20">
        <Sparkles size={16} /> {t("poster.create")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto border-border bg-card text-foreground" data-testid="poster-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("poster.title")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{t("poster.hint")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-2" data-testid="poster-format-switch">
            {FMT_BTN.map((f) => (
              <button key={f.k} data-testid={`poster-fmt-${f.k}`} onClick={() => setFmt(f.k)}
                className={`rounded-lg border px-2 py-2 text-xs font-bold transition ${fmt === f.k ? "border-[#1E5288] bg-[#1E5288] text-white" : "border-border bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {f.label}
              </button>
            ))}
          </div>

          {photos.length > 1 && (
            <div className="mt-2 min-w-0" data-testid="poster-photo-picker">
              <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{t("poster.choosePhoto")}</div>
              <div className="flex min-w-0 gap-2 overflow-x-auto pb-1">
                {photos.map((p, i) => (
                  <button key={i} type="button" data-testid={`poster-photo-${i}`} onClick={() => setPhotoIdx(i)}
                    className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition ${photoIdx === i ? "border-[#1E5288] ring-2 ring-[#1E5288]/40" : "border-border opacity-70 hover:opacity-100"}`}>
                    <img src={proxied(p)} alt={`photo ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-2 self-center flex w-full max-w-[240px] items-center justify-center overflow-hidden rounded-xl border border-border bg-muted"
            style={{ aspectRatio: `${FORMATS[fmt].W} / ${FORMATS[fmt].H}` }}>
            {busy && !dataUrl && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="poster-loading">
                <Loader2 className="animate-spin" size={18} /> {t("poster.generating")}
              </div>
            )}
            {dataUrl && (
              <img ref={previewRef} src={dataUrl} alt="poster" data-testid="poster-preview"
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
                className="h-full w-full object-contain cursor-move touch-none select-none" draggable={false} />
            )}
          </div>

          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-3" data-testid="poster-zoom-row">
              <span className="text-xs font-semibold text-muted-foreground">{t("poster.zoom")}</span>
              <input type="range" min="1" max="3" step="0.02" value={zoom} data-testid="poster-zoom"
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-secondary accent-[#1E5288]" />
              <button type="button" data-testid="poster-reset-frame" onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
                className="rounded-md border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground">
                {t("poster.reset")}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("poster.dragHint")}</p>
            {hasDealerLogo && (
              <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-foreground" data-testid="poster-dealer-toggle-row">
                <input type="checkbox" checked={showDealer} onChange={(e) => setShowDealer(e.target.checked)}
                  className="h-4 w-4 accent-[#1E5288]" data-testid="poster-dealer-toggle" />
                {t("poster.showDealerLogo")}
              </label>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <Button data-testid="poster-download-btn" onClick={download} disabled={!dataUrl} className="bg-[#1E5288] font-bold text-white hover:bg-[#0F2540]">
              <Download size={16} className="mr-2" /> {t("poster.download")}
            </Button>
            <Button data-testid="poster-share-btn" variant="outline" onClick={share} disabled={!dataUrl}>
              <Share2 size={16} className="mr-2" /> {t("poster.share")}
            </Button>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SocialPoster;
